import { useEffect, useRef, useState } from "react";
import { Paperclip, Send, Trash2, Download } from "lucide-react";

import { Badge } from "@monorepo-apps/ui/src/components/badge";
import { Button } from "@monorepo-apps/ui/src/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import {
  useDeleteFeatureRequest,
  useUpdateFeatureRequest,
} from "@/application/use-cases/feature-requests/use-feature-requests.queries";
import {
  useAddComment,
  useDeleteAttachment,
  useDeleteComment,
  useRequestAttachments,
  useRequestComments,
  useUploadAttachment,
} from "@/application/use-cases/feature-requests/use-feature-request-thread.queries";
import { featureRequestAttachmentsRepository } from "@/infrastructure/repositories/feature-request-attachments.supabase.repository";
import { useAuth } from "@/presentation/providers/auth.provider";
import { useWorkspace } from "@/presentation/providers/workspace.provider";
import {
  REQUEST_STATUSES,
  REQUEST_STATUS_LABEL,
  REQUEST_TYPE_LABEL,
  REQUEST_TYPE_TONE,
} from "@/presentation/components/requests/feature-request-meta";
import type {
  FeatureRequestAttachmentRow,
  FeatureRequestRow,
  FeatureRequestStatus,
} from "@/infrastructure/supabase/database.types";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB, matches the bucket limit

export function FeatureRequestDrawerContent({
  request,
  onDeleted,
}: {
  request: FeatureRequestRow;
  onDeleted: () => void;
}) {
  const { canWrite } = useWorkspace();
  const { session } = useAuth();
  const selfId = session?.user?.id ?? null;
  const isOwner = !!selfId && request.submitted_by === selfId;
  const update = useUpdateFeatureRequest();
  const del = useDeleteFeatureRequest();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const canEditContent = canWrite || isOwner;
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState(request.description ?? "");

  const saveDesc = () => {
    const next = descDraft.trim() ? descDraft.trim() : null;
    update.mutate(
      { id: request.id, patch: { description: next } },
      { onSuccess: () => setEditingDesc(false) }
    );
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            variant="outline"
            className="text-[10px]"
            style={{ color: REQUEST_TYPE_TONE[request.type] }}
          >
            {REQUEST_TYPE_LABEL[request.type]}
          </Badge>
          {canWrite ? (
            <Select
              value={request.status}
              onValueChange={(v) =>
                update.mutate({ id: request.id, patch: { status: v as FeatureRequestStatus } })
              }
            >
              <SelectTrigger className="h-7 w-36 text-[11px]" aria-label="Status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REQUEST_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="secondary" className="text-[10px]">
              {REQUEST_STATUS_LABEL[request.status]}
            </Badge>
          )}
        </div>
        <p className="text-muted-foreground/70 mt-2 text-[10.5px]">
          {request.submitter_email ?? "unknown"} · {request.created_at?.slice(0, 10)}
        </p>
      </div>

      {editingDesc ? (
        <div className="space-y-1.5">
          <Textarea
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            rows={8}
            className="text-[12px]"
            aria-label="Edit details"
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={saveDesc} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setDescDraft(request.description ?? "");
                setEditingDesc(false);
              }}
              className="text-muted-foreground text-[12px] hover:underline"
            >
              Cancel
            </button>
            {update.isError ? (
              <span className="text-destructive text-[10.5px]">Save failed.</span>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="group flex items-start justify-between gap-2">
          {request.description ? (
            <p className="text-foreground text-[13px] leading-relaxed whitespace-pre-wrap">
              {request.description}
            </p>
          ) : (
            <p className="text-muted-foreground text-[12px] italic">No details.</p>
          )}
          {canEditContent ? (
            <button
              type="button"
              onClick={() => {
                setDescDraft(request.description ?? "");
                setEditingDesc(true);
              }}
              className="text-primary shrink-0 text-[11px] font-semibold"
            >
              Edit
            </button>
          ) : null}
        </div>
      )}

      <AttachmentsSection requestId={request.id} canWrite={canWrite} selfId={selfId} />
      <CommentsSection requestId={request.id} canWrite={canWrite} selfId={selfId} />

      {canWrite || isOwner ? (
        <div className="border-border/60 mt-auto border-t pt-3">
          {confirmingDelete ? (
            <div className="flex items-center gap-2 text-[12px]">
              <span className="text-muted-foreground">Delete this item?</span>
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => del.mutate(request.id, { onSuccess: onDeleted })}
                className="text-destructive font-semibold disabled:opacity-40"
              >
                {del.isPending ? "Deleting…" : "Yes, delete"}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingDelete(false)}
                className="text-muted-foreground hover:underline"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="text-muted-foreground hover:text-destructive flex items-center gap-1.5 text-[12px]"
            >
              <Trash2 className="size-3.5" /> Delete
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}

function AttachmentsSection({
  requestId,
  canWrite,
  selfId,
}: {
  requestId: string;
  canWrite: boolean;
  selfId: string | null;
}) {
  const attachmentsQ = useRequestAttachments(requestId);
  const upload = useUploadAttachment();
  const del = useDeleteAttachment(requestId);
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const attachments = attachmentsQ.data ?? [];

  const onFiles = (files: FileList | null) => {
    if (!files || !selfId) return;
    setError(null);
    for (const file of Array.from(files)) {
      if (file.size > MAX_UPLOAD_BYTES) {
        setError(`"${file.name}" is over 10 MB.`);
        continue;
      }
      upload.mutate(
        { requestId, file, uploadedBy: selfId },
        { onError: () => setError(`Could not upload "${file.name}".`) }
      );
    }
    if (fileInput.current) fileInput.current.value = "";
  };

  return (
    <section className="border-border/60 border-t pt-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="eyebrow">Attachments ({attachments.length})</span>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={upload.isPending}
          className="text-primary flex items-center gap-1 text-[11px] font-semibold disabled:opacity-40"
        >
          <Paperclip className="size-3" /> {upload.isPending ? "Uploading…" : "Add file"}
        </button>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          onChange={(e) => onFiles(e.target.files)}
        />
      </div>
      {attachments.length === 0 ? (
        <p className="text-muted-foreground text-[11px] italic">
          No files. Attach a screenshot or document to illustrate.
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {attachments.map((a) => (
            <AttachmentItem
              key={a.id}
              attachment={a}
              canDelete={canWrite || a.uploaded_by === selfId}
              onDelete={() => del.mutate({ id: a.id, storage_path: a.storage_path })}
            />
          ))}
        </div>
      )}
      {error ? <p className="text-destructive mt-1 text-[10.5px]">{error}</p> : null}
    </section>
  );
}

function AttachmentItem({
  attachment,
  canDelete,
  onDelete,
}: {
  attachment: FeatureRequestAttachmentRow;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = (attachment.content_type ?? "").startsWith("image/");

  useEffect(() => {
    let alive = true;
    featureRequestAttachmentsRepository
      .signedUrl(attachment.storage_path)
      .then((u) => alive && setUrl(u))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [attachment.storage_path]);

  return (
    <div className="border-border bg-background relative w-28 overflow-hidden rounded-md border">
      {isImage && url ? (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={attachment.file_name} className="h-20 w-full object-cover" />
        </a>
      ) : (
        <a
          href={url ?? undefined}
          target="_blank"
          rel="noreferrer"
          className="text-muted-foreground hover:text-foreground flex h-20 w-full flex-col items-center justify-center gap-1"
        >
          <Download className="size-4" />
          <span className="text-[9px]">{url ? "Download" : "…"}</span>
        </a>
      )}
      <div className="flex items-center justify-between gap-1 px-1.5 py-1">
        <span className="truncate text-[9.5px]" title={attachment.file_name}>
          {attachment.file_name}
        </span>
        {canDelete ? (
          <button
            type="button"
            onClick={onDelete}
            aria-label={`Delete ${attachment.file_name}`}
            className="text-muted-foreground hover:text-destructive shrink-0"
          >
            <Trash2 className="size-3" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function CommentsSection({
  requestId,
  canWrite,
  selfId,
}: {
  requestId: string;
  canWrite: boolean;
  selfId: string | null;
}) {
  const { session } = useAuth();
  const commentsQ = useRequestComments(requestId);
  const add = useAddComment();
  const del = useDeleteComment(requestId);
  const [body, setBody] = useState("");
  const comments = commentsQ.data ?? [];

  const submit = () => {
    const text = body.trim();
    if (!text || !selfId) return;
    add.mutate(
      {
        request_id: requestId,
        body: text,
        author_id: selfId,
        author_email: session?.user?.email ?? null,
      },
      { onSuccess: () => setBody("") }
    );
  };

  return (
    <section className="border-border/60 border-t pt-3">
      <span className="eyebrow mb-2 block">Discussion ({comments.length})</span>
      <div className="flex flex-col gap-2.5">
        {comments.map((c) => (
          <div key={c.id} className="bg-muted/40 rounded-md px-2.5 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-foreground text-[11px] font-semibold">
                {c.author_email ?? "unknown"}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground/70 text-[10px]">
                  {c.created_at?.slice(0, 16).replace("T", " ")}
                </span>
                {canWrite || c.author_id === selfId ? (
                  <button
                    type="button"
                    onClick={() => del.mutate(c.id)}
                    aria-label="Delete comment"
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="size-3" />
                  </button>
                ) : null}
              </div>
            </div>
            <p className="text-foreground mt-1 text-[12px] leading-relaxed whitespace-pre-wrap">
              {c.body}
            </p>
          </div>
        ))}
        {comments.length === 0 ? (
          <p className="text-muted-foreground text-[11px] italic">
            No comments yet. Start the conversation.
          </p>
        ) : null}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder="Add a comment or update…"
          className="text-[12px]"
        />
        <Button size="sm" onClick={submit} disabled={!body.trim() || add.isPending}>
          <Send className="size-3.5" />
        </Button>
      </div>
    </section>
  );
}
